/**
 * Generic Tiptap toolbar UI primitives — extracted verbatim from NewTextTemplatePage.tsx (a
 * mechanical move, per the WYSIWYG editor plan's Part 1.8) so both the legacy HTML/Markdown
 * editor and the new CDM editor (CdmToolbar.tsx) share one implementation. None of these are tied
 * to a specific Tiptap schema — they take editor-agnostic callbacks.
 */
import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTable, faLink } from '@fortawesome/free-solid-svg-icons'
import type { useEditor } from '@tiptap/react'
import { colors, fontSize, radius } from '../../styles/theme'
import { Tooltip } from '../Tooltip'

export function TBtn({
  active, onClick, title, children, disabled,
}: { active?: boolean; onClick: () => void; title: string; children: React.ReactNode; disabled?: boolean }) {
  const [hover, setHover] = useState(false)
  return (
    <Tooltip text={title} show={hover && !disabled} position="top">
      <button
        type="button"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          padding: '3px 7px',
          border: `1px solid ${active ? colors.primary : colors.border}`,
          borderRadius: radius.sm,
          background: active ? colors.primaryLight : 'none',
          color: active ? colors.primary : colors.text,
          fontSize: fontSize.base,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: active ? 700 : 400,
          lineHeight: 1.4,
          minWidth: 28,
          opacity: disabled ? 0.35 : 1,
        }}
      >
        {children}
      </button>
    </Tooltip>
  )
}

export function Sep() {
  return <div style={{ width: 1, background: colors.border, height: 22, margin: '0 2px', flexShrink: 0 }} />
}

export function ColorBtn({ tooltipText, labelContent, defaultValue, onChange }: {
  tooltipText: string
  labelContent: React.ReactNode
  defaultValue?: string
  onChange: (color: string) => void
}) {
  const [hover, setHover] = useState(false)
  return (
    <Tooltip text={tooltipText} show={hover} position="top">
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: fontSize.sm, color: colors.textSecondary, cursor: 'pointer' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {labelContent}
        <input
          type="color"
          defaultValue={defaultValue}
          style={{ width: 20, height: 20, padding: 0, border: 'none', cursor: 'pointer', borderRadius: 2 }}
          onChange={e => onChange(e.target.value)}
        />
      </label>
    </Tooltip>
  )
}

const GRID_COLS = 6
const GRID_ROWS = 6

export function TableInsertBtn({ onInsert }: { onInsert: (rows: number, cols: number) => void }) {
  const [open, setOpen] = useState(false)
  const [hR, setHR] = useState(0)
  const [hC, setHC] = useState(0)
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined)

  function scheduleClose() {
    closeTimer.current = setTimeout(() => { setOpen(false); setHR(0); setHC(0) }, 120)
  }

  function cancelClose() {
    clearTimeout(closeTimer.current)
  }

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => { cancelClose(); setOpen(true) }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        style={{
          padding: '3px 7px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
          background: open ? colors.primaryLight : 'none', color: open ? colors.primary : colors.text,
          fontSize: fontSize.base, cursor: 'pointer', lineHeight: 1.4, minWidth: 28,
        }}
      >
        <FontAwesomeIcon icon={faTable} />
      </button>
      {open && (
        <div
          onMouseEnter={cancelClose}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
            background: colors.surface, border: `1px solid ${colors.border}`,
            borderRadius: radius.md, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            padding: '10px', userSelect: 'none',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_COLS}, 18px)`, gap: 3, marginBottom: 8 }}>
            {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, i) => {
              const row = Math.floor(i / GRID_COLS) + 1
              const col = (i % GRID_COLS) + 1
              const filled = row <= hR && col <= hC
              return (
                <div
                  key={i}
                  onMouseEnter={() => { setHR(row); setHC(col) }}
                  onClick={() => { onInsert(hR, hC); setOpen(false) }}
                  style={{
                    width: 18, height: 18, border: `1px solid ${filled ? colors.primary : colors.border}`,
                    borderRadius: 2, background: filled ? colors.primaryLight : colors.background,
                    cursor: 'pointer', transition: 'background 0.1s, border-color 0.1s',
                  }}
                />
              )
            })}
          </div>
          <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, textAlign: 'center' }}>
            {hR > 0 && hC > 0 ? `${hC} × ${hR}` : 'Hover to select size'}
          </div>
        </div>
      )}
    </div>
  )
}

const SYMBOL_GROUPS: { label: string; symbols: string[] }[] = [
  { label: 'Common', symbols: ['©', '®', '™', '§', '¶', '†', '‡', '•', '…', '–', '—', '·'] },
  { label: 'Currency', symbols: ['€', '£', '¥', '¢', '₿', '₹', '₩', '₪', '₺', '₱'] },
  { label: 'Math', symbols: ['±', '×', '÷', '≠', '≈', '≤', '≥', '∞', 'π', 'Σ', '√', '°', '½', '¼', '¾', '²', '³', 'µ'] },
  { label: 'Arrows', symbols: ['←', '→', '↑', '↓', '↔', '⇐', '⇒', '⇔', '↩', '↪'] },
  { label: 'Quotes', symbols: ['“', '”', '‘', '’', '«', '»', '‹', '›', '„', '‚'] },
  { label: 'Accented', symbols: ['à','á','â','ã','ä','å','æ','ç','è','é','ê','ë','ì','í','î','ï','ñ','ò','ó','ô','õ','ö','ø','ù','ú','û','ü','ý','ÿ','ß','œ'] },
  { label: 'Uppercase', symbols: ['À','Á','Â','Ã','Ä','Å','Æ','Ç','È','É','Ê','Ë','Ì','Í','Î','Ï','Ñ','Ò','Ó','Ô','Õ','Ö','Ø','Ù','Ú','Û','Ü','Ý','Ÿ','Œ'] },
]

export function SymbolBtn({ editor }: { editor: ReturnType<typeof useEditor> }) {
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  function insert(sym: string) {
    editor?.chain().focus().insertContent(sym).run()
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Tooltip text="Insert symbol" show={hover && !open} position="top">
        <button
          type="button"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() => setOpen(o => !o)}
          style={{
            padding: '3px 7px', border: `1px solid ${open ? colors.primary : colors.border}`,
            borderRadius: radius.sm, background: open ? colors.primaryLight : 'none',
            color: open ? colors.primary : colors.text, fontSize: fontSize.base,
            cursor: 'pointer', lineHeight: 1.4, minWidth: 28, fontWeight: open ? 700 : 400,
          }}
        >
          Ω
        </button>
      </Tooltip>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: colors.surface, border: `1px solid ${colors.border}`,
          borderRadius: radius.md, boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
          padding: '10px 12px', width: 320, maxHeight: 360, overflowY: 'auto',
        }}>
          {SYMBOL_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {group.symbols.map(sym => (
                  <button
                    key={sym}
                    type="button"
                    title={`U+${sym.codePointAt(0)?.toString(16).toUpperCase().padStart(4, '0')}`}
                    onClick={() => insert(sym)}
                    style={{
                      width: 28, height: 28, border: `1px solid ${colors.border}`,
                      borderRadius: radius.sm, background: colors.background,
                      fontSize: 14, cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.1s, border-color 0.1s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = colors.primaryLight; (e.currentTarget as HTMLElement).style.borderColor = colors.primary }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = colors.background; (e.currentTarget as HTMLElement).style.borderColor = colors.border }}
                  >
                    {sym}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function PopoverBtn({ icon, activeIcon, tooltipText, active, onActivate, onDeactivate, children }: {
  icon: typeof faLink
  /** Swapped in for `icon` while `active` is true — e.g. LinkBtn's link→unlink affordance.
   * Callers that just want the popover-open highlight (border/background, see below) without an
   * icon change omit this and keep their own icon throughout. */
  activeIcon?: typeof faLink
  tooltipText: string
  active: boolean
  onActivate: () => void
  onDeactivate: () => void
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Tooltip text={tooltipText} show={hover && !open} position="top">
        <button
          type="button"
          aria-label={tooltipText}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={() => { if (active) { onDeactivate(); setOpen(false) } else { onActivate(); setOpen(o => !o) } }}
          style={{
            padding: '3px 7px', border: `1px solid ${active ? colors.primary : colors.border}`,
            borderRadius: radius.sm, background: active ? colors.primaryLight : 'none',
            color: active ? colors.primary : colors.text, fontSize: fontSize.base,
            cursor: 'pointer', fontWeight: active ? 700 : 400, lineHeight: 1.4, minWidth: 28,
          }}
        >
          <FontAwesomeIcon icon={active && activeIcon ? activeIcon : icon} />
        </button>
      </Tooltip>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: colors.surface, border: `1px solid ${colors.border}`,
          borderRadius: radius.md, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          padding: '10px', minWidth: 280,
        }}>
          {children}
        </div>
      )}
    </div>
  )
}
